import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscription } from '@/hooks/useSubscription';
import { Calendar, CreditCard, RefreshCw, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const SubscriptionStatus: React.FC = () => {
  const navigate = useNavigate();
  const { 
    subscribed, 
    subscription_tier, 
    subscription_end, 
    refreshing, 
    checkSubscription, 
    manageSubscription 
  } = useSubscription();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = () => {
    if (!subscribed) return 'secondary';
    return 'default';
  };

  const getStatusText = () => {
    if (!subscribed) return 'Free Plan';
    return `${subscription_tier} Plan`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Subscription Status
            </CardTitle>
            <CardDescription>
              Manage your subscription and billing
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={checkSubscription}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Current Plan</p>
            <Badge variant={getStatusColor()} className="mt-1">
              {getStatusText()}
            </Badge>
          </div>
          
          {subscribed && subscription_end && (
            <div className="text-right">
              <p className="text-sm font-medium">Next Billing</p>
              <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                <Calendar className="h-3 w-3" />
                {formatDate(subscription_end)}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex gap-2">
          {subscribed ? (
            <Button 
              onClick={manageSubscription}
              className="flex-1"
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage Subscription
            </Button>
          ) : (
            <Button 
              onClick={() => navigate('/dashboard?tab=subscription')}
              className="flex-1"
            >
              Upgrade Plan
            </Button>
          )}
        </div>
        
        {!subscribed && (
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              You're currently on the free plan. Upgrade to unlock premium features, 
              access more AI bots, and get priority support.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};